import { DocumentType } from '@utils/enums.ts';

/**
 * Row status after a cancel (ZLAYT-1603).
 *
 * Cancelling a rerun of a finished report, deposition summary or demand
 * letter leaves the finished version as the current one, so the server then
 * reports Completed. The table keeps a cancelled row on "Draft" only while
 * the server still reports the run as going (a stale Pending); otherwise it
 * shows what the server says.
 */
const RUNNING_STATUSES = new Set(['pending', 'in progress', 'in_progress']);

export function cancelledRowStatus(serverStatus?: string | null): string {
  const value = String(serverStatus ?? '').trim();
  return RUNNING_STATUSES.has(value.toLowerCase()) ? 'Draft' : value;
}

/**
 * True when a socket status update should refetch the list: a row that
 * newly failed needs its failure reason, and a cancelled one needs the
 * status of the version that is current again.
 */
export function refetchAfterStatus(
  rawStatus: string | null | undefined,
  previousStatus: string | null | undefined
): boolean {
  const value = String(rawStatus ?? '').toLowerCase();
  if (value.includes('cancel')) return true;
  return value.includes('fail') && previousStatus !== 'Failed';
}

/**
 * What the Cancel confirmation says (ZLAYT-1642). For a report it says what
 * the server does on a cancel (use_case/services/instance.py,
 * cancel_use_case_generation): the run stops, nothing from it is kept, and
 * whatever it was charged is given back unless the report already has a
 * finished version, which a rerun was not charged for (ZLAYT-1526). The
 * other document types keep their question; their refund was not checked.
 */
export function cancelConfirmationText(document: {
  type: DocumentType;
  type_name?: string | null;
}): string {
  const name = document.type_name || 'document';
  if (document.type === DocumentType.USE_CASE) {
    return (
      `Stop generating this ${name}? Nothing from this run is kept, and ` +
      'anything it was charged is given back. You can generate it again later.'
    );
  }
  return `Are you sure you want to cancel the ${name} generation?`;
}
